// The live change feed, `GET /v1/feed?since=<seq>`, a WebSocket
// (crates/poulpe-server/src/feed/socket.rs). The server sends every change
// after the cursor, in order, then each new one as it commits: one text
// message per change, as `/v1/changes` lists them. The client sends nothing;
// the browser answers the server's pings itself. The server closes the socket
// with 4410 when the cursor is outside what the feed keeps (take a snapshot),
// and 4401 when the token or the session has ended.
//
// The socket is signed in as the HTTP requests are: by the session's cookie,
// which the browser sends to the page's own origin with the page's `Origin`,
// or by the bearer token the dev server adds (vite.config.ts). A WebSocket
// cannot carry a token the page holds, so a tab signed in by one does not
// follow the socket (main.tsx). Nor can it carry `Poulpe-As`: the member the
// pages act as goes as `&as=<member id>` instead, and the server refuses the
// upgrade when the session is another's.
import type { Change } from './types'

/** The close code that says the token or the session has ended. */
export const FEED_UNAUTHORIZED = 4401

/** The close code that says the feed cannot continue from the cursor: take a snapshot. */
export const FEED_SNAPSHOT_NEEDED = 4410

/** The close code that says the server restarts: connect again soon (Poulpe-659). */
export const FEED_RESTART = 1012

/** Reported when the server sent what is not a change: the client closed the socket. */
export const FEED_BAD_MESSAGE = 1007

/** Reported when the socket ended without a close frame: it failed, or never opened. */
export const FEED_ABNORMAL = 1006

export interface FeedHandlers {
  /** The socket is open: the changes after the cursor follow. */
  open(): void
  change(change: Change): void
  /** The socket closed, or could not open, with this code; never after `close()`. */
  closed(code: number): void
}

/** One connection to the live feed. */
export interface FeedSocket {
  /** Ends it: no handler is called after this. */
  close(): void
}

/**
 * Opens the live feed from a cursor, as the member the pages act as (`as`), once they know who is signed in,
 * and in the epoch of the snapshot the cursor follows, when known.
 */
export type OpenFeed = (since: number, handlers: FeedHandlers, as?: string | null, epoch?: string | null) => FeedSocket

/** The feed's URL on the page's own origin: `ws:` beside `http:`, `wss:` beside `https:`; with `as` and `epoch`, when given. */
export function feedUrl(
  since: number,
  as: string | null = null,
  page: Pick<Location, 'protocol' | 'host'> = window.location,
  epoch: string | null = null,
): string {
  const named = as ? `&as=${encodeURIComponent(as)}` : ''
  const of = epoch ? `&epoch=${encodeURIComponent(epoch)}` : ''
  return `${page.protocol === 'https:' ? 'wss:' : 'ws:'}//${page.host}/v1/feed?since=${since}${named}${of}`
}

/** A change, from a message's text; null when it is not one. */
export function parseChange(data: unknown): Change | null {
  if (typeof data !== 'string') return null
  let value: unknown
  try {
    value = JSON.parse(data)
  } catch {
    return null
  }
  if (typeof value !== 'object' || value === null) return null
  const { seq, entity, mutation_id, data: row } = value as Record<string, unknown>
  const ok =
    Number.isSafeInteger(seq) &&
    (seq as number) > 0 &&
    typeof entity === 'string' &&
    typeof mutation_id === 'string' &&
    typeof row === 'object' &&
    row !== null &&
    typeof (row as { id?: unknown }).id === 'string'
  return ok ? (value as Change) : null
}

export interface WebSocketFeedOptions {
  /** The feed's URL for a cursor and a member; the page's own origin by default. */
  url?: (since: number, as: string | null, epoch: string | null) => string
  /** The WebSocket class; the browser's by default. */
  WebSocket?: new (url: string) => WebSocket
}

/** The live feed over the browser's WebSocket. */
export function webSocketFeed({
  url = (since, as, epoch) => feedUrl(since, as, window.location, epoch),
  WebSocket: Socket = globalThis.WebSocket,
}: WebSocketFeedOptions = {}): OpenFeed {
  return (since, handlers, as = null, epoch = null) => {
    const ws = new Socket(url(since, as, epoch))
    let ended = false
    const end = (code: number) => {
      if (ended) return
      ended = true
      handlers.closed(code)
    }
    ws.onopen = () => {
      if (!ended) handlers.open()
    }
    ws.onmessage = (event: MessageEvent) => {
      if (ended) return
      const change = parseChange(event.data)
      if (change) {
        handlers.change(change)
        return
      }
      ws.close()
      end(FEED_BAD_MESSAGE)
    }
    // An error is followed by a close, which says how it ended.
    ws.onclose = (event: CloseEvent) => end(event.code || FEED_ABNORMAL)
    return {
      close() {
        if (ended) return
        ended = true
        ws.close()
      },
    }
  }
}
